import React from "react";
import { useTranslation } from "react-i18next";
import GeneralInformation from "./components/GeneralInformation";
import {
  ComponentDetailsByFileOut,
  EPDDocument,
  InventoryProductDetailsByFileOut,
} from "api-client";
import CircularityInformation from "./components/CircularityInformation";
import ComplianceInformation from "./components/ComplianceInformation";
import DocumentsTable from "./features/DocumentsTable";
import OwnProperties from "./components/OwnProperties";
import ReferenceUnit from "./components/ReferenceUnit";
import { IconCube, IconStack2 } from "@tabler/icons-react";
import clsx from "clsx";
import OwnDocuments from "./components/OwnDocuments";
import { searchStore } from "features/ProductSearch/SearchStore";

export interface Section {
  id: string;
  name: string;
  content: React.ReactNode;
  ref: React.MutableRefObject<null | HTMLLIElement>;
  hidden?: boolean;
}

export interface EPDDocuments extends EPDDocument {
  epd_name?: string;
}

const useDetailsSections = (
  details:
    | InventoryProductDetailsByFileOut
    | ComponentDetailsByFileOut
    | undefined
) => {
  const { t } = useTranslation();
  const [documentsList, setDocumentsList] = React.useState<
    undefined | EPDDocuments[]
  >(undefined);

  function getProductIcon(
    className?: string,
    diagram_url?: string | null
  ): React.ReactNode {
    return (
      <div className="flex items-center">
        {diagram_url ? (
          <img
            src={diagram_url}
            className="w-[52px] max-w-[52px] max-h-[52px] mr-4"
          />
        ) : null}
        {(details as ComponentDetailsByFileOut)?.type === "COMPONENT" ? (
          <IconStack2
            stroke={3}
            className={clsx(
              "bg-cyan-300 rounded-full p-[5px] min-w-6 min-h-6 mr-2 text-cyan-900",
              className
            )}
          />
        ) : (
          <IconCube
            stroke={3}
            className={clsx(
              "bg-teal-300 rounded-full p-[5px] min-w-6 min-h-6 mr-2 text-teal-900",
              className
            )}
          />
        )}
      </div>
    );
  }

  const currentEPDGroup =
    details?.epd_groups ?? searchStore.currentDetailNode?.epd_groups ?? [];

  function getEPDsDocuments() {
    const documents = currentEPDGroup
      ?.map((group) => group.epds)
      .flat()
      .map((rec) =>
        rec?.epd?.documents?.map((doc) => ({
          ...doc,
          epd_name: rec.epd?.name_de,
        }))
      )
      .flat()
      .filter(Boolean);
    setDocumentsList(documents as []);
  }

  const epdsCounts = currentEPDGroup
    .map((group) => group.epds)
    .flat()
    .map((rec) => rec?.epd?.name_de)
    .filter(Boolean).length;

  React.useEffect(() => {
    getEPDsDocuments();
  }, [epdsCounts]);

  const sectionItems: Section[] = [
    {
      id: "general",
      name: t("mapping.general"),
      content: <GeneralInformation details={details} />,
      ref: React.useRef(null),
    },
    {
      id: "conformity",
      name: t("mapping.conformity"),
      content: <ComplianceInformation details={details} />,
      ref: React.useRef(null),
    },
    {
      id: "circularity",
      name: t("productEditor.circularity"),
      content: (
        <CircularityInformation
          details={details as ComponentDetailsByFileOut}
        />
      ),
      ref: React.useRef(null),
    },
    {
      id: "documents",
      name: t("mapping.document"),
      content: (
        <DocumentsTable
          epdGroups={details?.epd_groups as []}
          epdDocuments={documentsList}
        />
      ),
      ref: React.useRef(null),
      hidden:
        (details as ComponentDetailsByFileOut)?.type === "COMPONENT" ||
        documentsList?.length === 0,
    },
    {
      id: "properties",
      name: t("mapping.ownProperties"),
      content: <OwnProperties details={details as ComponentDetailsByFileOut} />,
      ref: React.useRef(null),
      hidden: !Object.values(details?.org_properties ?? {}).length,
    },
    {
      id: "own-documents",
      name: t("mapping.ownDocuments"),
      content: (
        <OwnDocuments details={details as InventoryProductDetailsByFileOut} />
      ),
      ref: React.useRef(null),
      hidden:
        (details as InventoryProductDetailsByFileOut)?.files_info?.documents
          .length === 0,
    },
    {
      id: "environmental-data",
      name: t("productEditor.refUnitEnvironmentalData"),
      content: <ReferenceUnit details={details as ComponentDetailsByFileOut} />,
      ref: React.useRef(null),
    },
  ];

  return { sectionItems, getProductIcon };
};

export default useDetailsSections;
